import * as React from "react";
import { useLocation } from "wouter";
import { ChevronLeft, ChevronRight, Circle, FileText } from "lucide-react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addMonths,
  subMonths,
  isSameDay,
  isSameMonth,
  format,
} from "date-fns";
import { useListTasks, useListNotes } from "@workspace/api-client-react";
import { cn } from "@/lib/utils";
import { stripHtml } from "@/lib/strip-html";

export function CalendarPage() {
  const [, setLocation] = useLocation();
  const [month, setMonth] = React.useState(() => new Date());
  const [selectedDay, setSelectedDay] = React.useState(() => new Date());

  const { data: tasks = [] } = useListTasks();
  const { data: notes = [] } = useListNotes();

  const days = React.useMemo(() => {
    const start = startOfWeek(startOfMonth(month));
    const end = endOfWeek(endOfMonth(month));
    return eachDayOfInterval({ start, end });
  }, [month]);

  const tasksByDay = (day: Date) =>
    tasks.filter((t) => t.dueAt && isSameDay(new Date(t.dueAt), day));
  const notesByDay = (day: Date) =>
    notes.filter((n) => isSameDay(new Date(n.createdAt), day));

  const selectedTasks = tasksByDay(selectedDay);
  const selectedNotes = notesByDay(selectedDay);

  return (
    <div className="max-w-4xl mx-auto min-h-[calc(100vh-4rem)] flex flex-col px-4">
      <div className="flex items-center justify-between pt-4 pb-2 shrink-0">
        <button
          type="button"
          onClick={() => setMonth((m) => subMonths(m, 1))}
          className="p-2 rounded-full hover:bg-muted transition-colors"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-semibold">{format(month, "MMMM yyyy")}</h1>
        <button
          type="button"
          onClick={() => setMonth((m) => addMonths(m, 1))}
          className="p-2 rounded-full hover:bg-muted transition-colors"
          aria-label="Next month"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      <div className="grid grid-cols-7 text-center text-xs font-medium text-muted-foreground pb-2 shrink-0">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={i}>{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1 shrink-0">
        {days.map((day) => {
          const dayTasks = tasksByDay(day);
          const dayNotes = notesByDay(day);
          const inMonth = isSameMonth(day, month);
          const isSelected = isSameDay(day, selectedDay);
          return (
            <button
              key={day.toISOString()}
              type="button"
              onClick={() => setSelectedDay(day)}
              className={cn(
                "aspect-square rounded-xl flex flex-col items-center justify-center gap-0.5 text-sm transition-colors",
                isSelected
                  ? "gradient-primary text-white"
                  : inMonth
                    ? "hover:bg-muted"
                    : "text-muted-foreground/40 hover:bg-muted/50",
              )}
            >
              <span>{format(day, "d")}</span>
              <div className="flex items-center gap-0.5">
                {dayTasks.length > 0 && (
                  <span
                    className={cn(
                      "h-1 w-1 rounded-full",
                      isSelected ? "bg-white" : "bg-primary",
                    )}
                  />
                )}
                {dayNotes.length > 0 && (
                  <span
                    className={cn(
                      "h-1 w-1 rounded-full",
                      isSelected ? "bg-white/70" : "bg-muted-foreground",
                    )}
                  />
                )}
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex-1 overflow-y-auto pt-4 pb-6">
        <h2 className="text-sm font-semibold text-muted-foreground mb-2">
          {format(selectedDay, "EEEE, MMMM d")}
        </h2>
        {selectedTasks.length === 0 && selectedNotes.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            Nothing on this day.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {selectedTasks.map((task) => (
              <div
                key={`task-${task.id}`}
                className="flex items-center gap-2 bg-card border rounded-xl px-4 py-3"
              >
                <Circle className="h-4 w-4 text-primary shrink-0" />
                <div className="min-w-0">
                  <p
                    className={cn(
                      "text-sm font-medium truncate",
                      task.completed && "line-through text-muted-foreground",
                    )}
                  >
                    {task.title}
                  </p>
                  {task.dueAt && (
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(task.dueAt), "h:mm a")}
                    </p>
                  )}
                </div>
              </div>
            ))}
            {selectedNotes.map((note) => (
              <button
                key={`note-${note.id}`}
                type="button"
                onClick={() => setLocation(`/browse/${note.id}`)}
                className="flex items-center gap-2 bg-card border rounded-xl px-4 py-3 text-left"
              >
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    {note.title || stripHtml(note.content)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(note.createdAt), "h:mm a")}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
